import { useQuery } from '@tanstack/react-query';
import { container } from '@/app/container';
import { SalesRepositoryToken } from '../../di/tokens';

/**
 * Hooks por aba do detalhe do contrato. Mesma disciplina do detalhe da
 * transação: carregam sob demanda (`enabled`) e `retry: false` porque um elo da
 * cadeia (BA atual, offer) pode legitimamente não existir.
 */

export function useContract(contractId: string, enabled = true) {
  const repository = container.resolve(SalesRepositoryToken);
  return useQuery({
    queryKey: ['sales', 'contract', contractId],
    queryFn: () => repository.getContract(contractId),
    staleTime: 1000 * 60 * 5,
    enabled: !!contractId && enabled,
    retry: false,
  });
}

export function useContractInvoices(contractId: string, enabled = true) {
  const repository = container.resolve(SalesRepositoryToken);
  return useQuery({
    queryKey: ['sales', 'contract', contractId, 'invoices'],
    queryFn: () => repository.getContractInvoices(contractId),
    staleTime: 1000 * 60 * 5,
    enabled: !!contractId && enabled,
    retry: false,
  });
}

/**
 * Tentativas de cobrança de uma fatura — bloco do modal "Resumo da Fatura".
 * Carrega só quando o modal monta (o componente só existe com uma fatura
 * selecionada), uma request por fatura aberta.
 */
export function useInvoiceTransactions(invoiceId: string) {
  const repository = container.resolve(SalesRepositoryToken);
  return useQuery({
    queryKey: ['sales', 'invoice', invoiceId, 'transactions'],
    queryFn: () => repository.getInvoiceTransactions(invoiceId),
    staleTime: 1000 * 60 * 5,
    enabled: !!invoiceId,
    retry: false,
  });
}

export function useContractOfferAndArrangements(
  contractId: string,
  enabled = true,
) {
  const repository = container.resolve(SalesRepositoryToken);
  return useQuery({
    queryKey: ['sales', 'contract', contractId, 'offer-arrangements'],
    queryFn: () => repository.getContractOfferAndArrangements(contractId),
    staleTime: 1000 * 60 * 5,
    enabled: !!contractId && enabled,
    retry: false,
  });
}

/** Todas as BAs do contrato — modal "Ver acordos de pagamento atrelados". */
export function useContractBillingAgreements(
  contractId: string,
  enabled = true,
) {
  const repository = container.resolve(SalesRepositoryToken);
  return useQuery({
    queryKey: ['sales', 'contract', contractId, 'billing-agreements'],
    queryFn: () => repository.getContractBillingAgreements(contractId),
    staleTime: 1000 * 60 * 5,
    enabled: !!contractId && enabled,
    retry: false,
  });
}

/**
 * Detalhe de uma BA com **todas** as invoices dela (sem filtro de status).
 * Base da página `/billing-agreements/:id` e do modal de renegociação — que
 * precisa enxergar as invoices `cancelled` da BA para oferecê-las como fonte da
 * dívida (cancelar uma BA marca as `pending` dela como `cancelled`).
 */
export function useBillingAgreement(
  billingAgreementId: string,
  enabled = true,
) {
  const repository = container.resolve(SalesRepositoryToken);
  return useQuery({
    queryKey: ['sales', 'billing-agreement', billingAgreementId],
    queryFn: () => repository.getBillingAgreement(billingAgreementId),
    staleTime: 1000 * 60 * 5,
    enabled: !!billingAgreementId && enabled,
    retry: false,
  });
}

/** Página `/debt-arrangements/:id`: a DA com as BAs substituída e resultante. */
export function useDebtArrangement(debtArrangementId: string) {
  const repository = container.resolve(SalesRepositoryToken);
  return useQuery({
    queryKey: ['sales', 'debt-arrangement', debtArrangementId],
    queryFn: () => repository.getDebtArrangement(debtArrangementId),
    staleTime: 1000 * 60 * 5,
    enabled: !!debtArrangementId,
    retry: false,
  });
}

/**
 * O alerta de pagamento duplicado que encerrou esta fatura ("não abrir de novo
 * para esta fatura"), se houver. No máximo um importa: reabrir limpa a marca, e
 * a próxima detecção abre um alerta novo em vez de procurar outro encerrado.
 */
export function useInvoiceClosedForDuplicatePayment(invoiceId: string) {
  const repository = container.resolve(SalesRepositoryToken);
  return useQuery({
    queryKey: ['sales', 'operational-alerts', 'closed-for-entity', invoiceId],
    queryFn: async () => {
      const { items } = await repository.listOperationalAlerts({
        type: 'duplicate_payment',
        entityType: 'Invoice',
        entityId: invoiceId,
        closedForEntity: true,
        perPage: 1,
      });
      return items[0] ?? null;
    },
    // Sem `staleTime`: o encerramento nasce na Central de Pendências, que não
    // invalida esta chave — com cache, a fatura diria "vigiada" logo depois.
    enabled: !!invoiceId,
    retry: false,
  });
}
